import {
  type FeatureContext,
  Features,
  featureOperations,
} from '@ardoq/features';
import {
  ARDOQ_DEFAULT_LOCALE,
  getLocale,
  type LocaleCode,
} from '@ardoq/locale';

export type SupportedLanguageOption = {
  value: string;
  label: string;
};

export const DEFAULT_LANGUAGE = 'en';

/**
 * The canonical regional-format locale to apply by default for a UI language.
 *
 * The locale is resolved from `@ardoq/locale`'s shared `localeList` (the same
 * source that backs the regional-format dropdown), so any language whose code
 * matches a locale entry stays in sync without a hand-maintained map. `en` has
 * no plain locale entry (date-fns only ships regional variants such as
 * `en-US`), so it falls back to the Ardoq default locale. Languages without a
 * matching locale entry return `undefined`. The user can still pick a different
 * format locale afterwards.
 */
export const getDefaultLocaleForLanguage = (
  language: string
): LocaleCode | undefined => {
  if (language === DEFAULT_LANGUAGE) return ARDOQ_DEFAULT_LOCALE.code;
  return getLocale(language)?.code;
};

export const SUPPORTED_LANGUAGES: ReadonlyArray<SupportedLanguageOption> = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
];

const BETA_LANGUAGES: ReadonlyArray<SupportedLanguageOption> = [];

export const ALL_LANGUAGE_CODES: ReadonlyArray<string> = [
  ...SUPPORTED_LANGUAGES,
  ...BETA_LANGUAGES,
].map(option => option.value);

export const getAvailableLanguages = (
  featureContext: FeatureContext
): SupportedLanguageOption[] =>
  featureOperations.hasFeature(featureContext, Features.LANGUAGE_SELECTION)
    ? [...SUPPORTED_LANGUAGES, ...BETA_LANGUAGES]
    : [...SUPPORTED_LANGUAGES];
